import React, { RefObject } from 'react';
import {
  Button,
  Paper,
  Typography,
  Menu,
  MenuItem as MuiMenuItem,
  CircularProgress,
  Chip,
  Grid,
  Box,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import SaveIcon from '@mui/icons-material/Save';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import MathQuestionBlock from './MathQuestionBlock';
import type { Question } from './mathWordProblemsTypes';

const streamingMarkdownSx = {
  '& h1, & h2, & h3': {
    color: '#9c27b0',
    fontWeight: 700,
    mt: 3,
    mb: 2,
  },
  '& h1': { fontSize: '1.75rem', textAlign: 'center', mb: 3 },
  '& h2': { fontSize: '1.25rem', borderBottom: '1px solid #e0e0e0', pb: 1 },
  '& h3': { fontSize: '1.1rem' },
  '& ul, & ol': { pl: 3, mb: 2 },
  '& li': { mb: 1, lineHeight: 1.8 },
  '& strong': { color: '#7b1fa2' },
  '& p': { lineHeight: 1.8, mb: 1.5 },
  '& hr': { my: 2, borderColor: '#e0e0e0' },
  '& em': { color: '#666', fontStyle: 'italic' },
  '& .katex': { fontSize: '1.1em' },
};

export interface MathProblemsDisplayProps {
  isStreaming: boolean;
  streamingQuestionsContent: string;
  streamingAnswerKeyContent: string;
  questions: Question[];
  answerKey: string;
  gradeLevel: string;
  graphCount: number;
  error: string | null;
  anchorEl: HTMLElement | null;
  open: boolean;
  isSaving: boolean;
  isCapturingGraphs: boolean;
  captureContainerRef: RefObject<HTMLDivElement | null>;
  handlePrint: () => void;
  handleDownloadClick: (event: React.MouseEvent<HTMLElement>) => void;
  handleDownloadClose: () => void;
  handleDownloadPdf: () => void;
  handleDownloadWord: () => void;
  handleSave: () => void;
}

const MathProblemsDisplay: React.FC<MathProblemsDisplayProps> = ({
  isStreaming,
  streamingQuestionsContent,
  streamingAnswerKeyContent,
  questions,
  answerKey,
  gradeLevel,
  graphCount,
  error,
  anchorEl,
  open,
  isSaving,
  isCapturingGraphs,
  captureContainerRef,
  handlePrint,
  handleDownloadClick,
  handleDownloadClose,
  handleDownloadPdf,
  handleDownloadWord,
  handleSave,
}) => (
  <>
    {isStreaming && streamingQuestionsContent && (
      <Paper elevation={3} sx={{ p: 4, mb: 3 }}>
        <Typography variant="h5" sx={{ mb: 3, color: '#9c27b0', fontWeight: 700, textAlign: 'center' }}>
          Math Word Problems
        </Typography>
        <Box sx={streamingMarkdownSx}>
          <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>
            {streamingQuestionsContent}
          </ReactMarkdown>
        </Box>

        {streamingAnswerKeyContent && (
          <Box
            sx={{
              mt: 4,
              pt: 2,
              borderTop: '2px solid #9c27b0',
              ...streamingMarkdownSx,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, color: '#9c27b0', fontWeight: 700 }}>
              Answer Key
            </Typography>
            <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>
              {streamingAnswerKeyContent}
            </ReactMarkdown>
          </Box>
        )}
      </Paper>
    )}

    {!isStreaming && questions.length > 0 && (
      <Paper elevation={3} sx={{ p: 4, mb: 3 }} id="math-problems-content">
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 3, flexWrap: 'wrap' }} className="no-print">
          <Chip
            size="small"
            color={graphCount > 0 ? 'secondary' : 'default'}
            variant="outlined"
            label={`Graphs: ${graphCount}`}
            sx={{ mr: 'auto' }}
          />
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint} size="small">
            Print
          </Button>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadClick} size="small">
            Download
          </Button>
          <Menu anchorEl={anchorEl} open={open} onClose={handleDownloadClose}>
            <MuiMenuItem onClick={handleDownloadPdf}>
              <PictureAsPdfIcon sx={{ mr: 1 }} /> PDF
            </MuiMenuItem>
            <MuiMenuItem onClick={handleDownloadWord}>
              <DescriptionIcon sx={{ mr: 1 }} /> Word (.docx)
            </MuiMenuItem>
          </Menu>
          <Button
            variant="contained"
            startIcon={isSaving ? <CircularProgress size={18} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={isSaving || questions.length === 0}
            size="small"
            color="secondary"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </Box>

        <Box
          sx={{
            '& p': { lineHeight: 1.8, mb: 1 },
            '& .katex': { fontSize: '1.1em' },
          }}
        >
          <Typography variant="h5" sx={{ mb: 4, color: '#9c27b0', fontWeight: 700, textAlign: 'center' }}>
            Math Word Problems - Grade {gradeLevel}
          </Typography>

          {questions.map((question, index) => (
            <MathQuestionBlock key={index} question={question} index={index} />
          ))}
        </Box>

        {answerKey && (
          <Box
            className="answer-key-section"
            sx={{
              mt: 4,
              pt: 3,
              borderTop: '2px solid #9c27b0',
              '& h1, & h2, & h3': {
                color: '#9c27b0',
                fontWeight: 700,
                mt: 3,
                mb: 2,
              },
              '& h1': { fontSize: '1.75rem', textAlign: 'center', mb: 3 },
              '& h2': { fontSize: '1.25rem', borderBottom: '1px solid #e0e0e0', pb: 1 },
              '& h3': { fontSize: '1.1rem' },
              '& ul, & ol': { pl: 3, mb: 2 },
              '& li': { mb: 1, lineHeight: 1.8 },
              '& strong': { color: '#7b1fa2' },
              '& p': { lineHeight: 1.8, mb: 1.5 },
              '& hr': { my: 3, borderColor: '#e0e0e0' },
              '& code': {
                bgcolor: '#f5f5f5',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.9em',
              },
              '& .katex': { fontSize: '1.1em' },
            }}
          >
            <Typography variant="h5" sx={{ mb: 3, color: '#9c27b0', fontWeight: 700, textAlign: 'center' }}>
              Comprehensive Answer Key
            </Typography>
            <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>
              {answerKey}
            </ReactMarkdown>
          </Box>
        )}

        {!answerKey && questions.length > 0 && (
          <Box sx={{ mt: 4, pt: 3, borderTop: '2px solid #9c27b0' }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#9c27b0', fontWeight: 700 }}>
              Answer Key
            </Typography>
            <Grid container spacing={1}>
              {questions.map((q, idx) => (
                <Grid item xs={6} sm={4} md={3} key={idx}>
                  <Typography variant="body2">
                    {idx + 1}. {q.correctAnswer}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Paper>
    )}

    {!isStreaming && questions.length === 0 && !error && (
      <Paper elevation={1} sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          Ready to Generate Math Word Problems
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Select a grade level and standards, then click &quot;Generate Problems&quot; to create math word problems.
        </Typography>
      </Paper>
    )}

    {isCapturingGraphs && (
      <Box
        id="math-problems-capture"
        ref={captureContainerRef}
        sx={{
          position: 'fixed',
          left: -9999,
          top: 0,
          width: 800,
          visibility: 'hidden',
          pointerEvents: 'none',
          zIndex: -1,
          '& p': { lineHeight: 1.8, mb: 1 },
          '& .katex': { fontSize: '1.1em' },
        }}
      >
        {questions.map((question, index) => (
          <MathQuestionBlock key={index} question={question} index={index} />
        ))}
      </Box>
    )}
  </>
);

export default MathProblemsDisplay;
